import { getLocalApi } from 'app/shared/api/localApi';
import apiConfig from 'app/api/apiConfig.json';
import { Product, ProductType } from 'app/shared/model/Product';

export type ProductNameRow = {
    code: string;
    name: string;
    type: ProductType;
};

function canonicalProductType(typeName: string): ProductType {
    const n = String(typeName || '').toLowerCase();
    if (n === 'accommodation' || n === 'accommodationroom') return ProductType.Accommodation;
    if (n === 'addon' || n === 'add-on') return ProductType.AddOn;
    if (n === 'transfer') return ProductType.Transfer;
    if (n === 'package') return ProductType.Package;
    return ProductType.Course;
}

function extractArray(data: unknown): unknown[] {
    if (Array.isArray(data)) return data;
    const o = data as Record<string, unknown> | null;
    if (!o) return [];
    const nested = o.data ?? o.items ?? o.result ?? o.Data;
    return Array.isArray(nested) ? nested : [];
}

export async function getProductNames(codes: string[]): Promise<Product[]> {
    const uniqueCodes = [...new Set(codes.map((code) => String(code || '').trim()).filter(Boolean))];
    if (uniqueCodes.length === 0) return [];

    const localApi = await getLocalApi();
    const response = await localApi.call(
        apiConfig.products_names,
        'POST',
        JSON.stringify({ ids: uniqueCodes })
    );
    if (!response.ok) {
        throw new Error(`Failed to fetch product names (HTTP ${response.status})`);
    }

    const rows = extractArray(await response.json());
    return rows
        .map((raw) => {
            const row = raw as Record<string, unknown>;
            const code = String(row.code ?? row.Code ?? '').trim();
            const name = String(row.name ?? row.Name ?? '').trim();
            if (!code || !name) return undefined;
            return {
                code,
                name,
                type: canonicalProductType(String(row.type ?? row.Type ?? '')),
                active: true,
            } as Product;
        })
        .filter((product): product is Product => !!product);
}

export default getProductNames;
